import { AnimatePresence, motion } from "motion/react"
import { createContext, ReactNode, useContext, useId, useState } from "react"
import {
  Key,
  Tab,
  TabList,
  TabListProps,
  TabProps,
  Tabs,
} from "react-aria-components"

import { cn } from "@/utilities/etc"

import styles from "./FilterTabs.module.css"

interface FilterTabsProps<T extends object> extends TabListProps<T> {
  onSelectionChange: (key: Key) => void
  selectedKey?: Key
  defaultSelectedKey?: Key
}

/**
 * The distance, in pixels, that panel content travels horizontally while
 * sliding in or out.
 */
const PANEL_SLIDE_DISTANCE = 24

/**
 * Scopes the sliding selection pill to a single FilterTabs instance, so two tab
 * bars on the same page do not animate their pills into each other.
 */
const PillLayoutIDContext = createContext<string | null>(null)

const FilterTabs = <T extends object>(props: FilterTabsProps<T>) => {
  const pillLayoutID = useId()

  return (
    <PillLayoutIDContext.Provider value={pillLayoutID}>
      <Tabs
        onSelectionChange={props.onSelectionChange}
        selectedKey={props.selectedKey}
        defaultSelectedKey={props.defaultSelectedKey}
        orientation="horizontal"
      >
        <TabList
          {...props}
          className={cn(props.className, styles.filterTabList)}
        >
          {props.children}
        </TabList>
      </Tabs>
    </PillLayoutIDContext.Provider>
  )
}

const FilterTab = (props: TabProps) => {
  const pillLayoutID = useContext(PillLayoutIDContext)

  return (
    <Tab {...props} className={cn(props.className, styles.filterTab)}>
      {(renderProps) => (
        <>
          {renderProps.isSelected && (
            <motion.span
              aria-hidden
              className={styles.selectedPill}
              layoutId={`filter-tabs-pill-${pillLayoutID}`}
              transition={{ type: "spring", stiffness: 400, damping: 34 }}
            />
          )}
          <span className={cn("body--2", "semi-bold", styles.filterTabLabel)}>
            {typeof props.children === "function"
              ? props.children(renderProps)
              : props.children}
          </span>
        </>
      )}
    </Tab>
  )
}

interface FilterTabPanelsProps {
  /**
   * The tab ids in the order they are rendered. Used to work out whether the
   * newly selected tab sits to the left or the right of the previous one, so
   * the panel slides in the matching direction.
   */
  tabOrder: Key[]
  selectedKey: Key
  children: ReactNode
  className?: string
}

const panelVariants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: direction * PANEL_SLIDE_DISTANCE,
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -PANEL_SLIDE_DISTANCE,
  }),
}

const FilterTabPanels = (props: FilterTabPanelsProps) => {

  const [previousKey, setPreviousKey] = useState(props.selectedKey)
  const [direction, setDirection] = useState(1)

  if (previousKey !== props.selectedKey) {
    const previousIndex = props.tabOrder.indexOf(previousKey)
    const selectedIndex = props.tabOrder.indexOf(props.selectedKey)
    setDirection(selectedIndex >= previousIndex ? 1 : -1)
    setPreviousKey(props.selectedKey)
  }

  return (
    <AnimatePresence mode="wait" custom={direction} initial={false}>
      <motion.div
        key={String(props.selectedKey)}
        className={props.className}
        custom={direction}
        variants={panelVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        {props.children}
      </motion.div>
    </AnimatePresence>
  )
}

FilterTabs.Tab = FilterTab
FilterTabs.Panels = FilterTabPanels

export default FilterTabs
